'use client'

/**
 * AshraryaWidget — the floating chat button + panel container.
 *
 * Only UI surface the host app directly renders. Composes:
 *   - Floating launch button (theme-coloured, fixed bottom-right)
 *   - Chat panel             (ChatPanel — message thread + input)
 *
 * Path-based opt-out: respects config.hideOnPaths (defaults to none).
 */

import { useState, useEffect } from 'react'
import { useAshraryaConfig } from './AshraryaProvider'
import { ChatPanel } from './components/ChatPanel'
import { useTourEngine } from './components/TourEngine'

export function AshraryaWidget() {
  const config = useAshraryaConfig()
  const { isActive: tourActive } = useTourEngine()
  const [isOpen, setIsOpen] = useState(false)
  const [currentPath, setCurrentPath] = useState('')
  // On phones the host can ask the bubble to step aside (body data flags set
  // while a sheet or the menu drawer is open) — it was sitting on top of
  // Add buttons and menu rows.
  const [shy, setShy] = useState(false)

  // After a tour ends we return to the default state — bubble only, panel
  // closed. The panel was already closed via onMinimise() at tour start.
  void tourActive

  // Relay handoff: when a tour step spotlights one of the suggestion cards
  // INSIDE this panel (target ids 'ashraya-card-*'), the panel must be open
  // or the target can never appear. For every OTHER step the panel steps
  // aside — on phones it fills the screen and would bury the tour.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onTourEvent = (e) => {
      const d = e.detail || {}
      if (d.type !== 'step_started' && d.type !== 'target_missing') return
      if (typeof d.target_id !== 'string') return
      if (d.target_id.startsWith('ashraya-card-')) {
        setIsOpen(true)
      } else if (d.type === 'step_started') {
        setIsOpen(false)
      }
    }
    window.addEventListener('ashraya:tour', onTourEvent)
    return () => window.removeEventListener('ashraya:tour', onTourEvent)
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const check = () =>
      setShy(!!(document.body.dataset.ashrayaShy || document.body.dataset.ashrayaShyNav))
    check()
    const mo = new MutationObserver(check)
    mo.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-ashraya-shy', 'data-ashraya-shy-nav'],
    })
    return () => mo.disconnect()
  }, [])

  // Track the current path so `hideOnPaths` works without a router dep.
  useEffect(() => {
    if (typeof window === 'undefined') return
    setCurrentPath(window.location.pathname)
    const handler = () => setCurrentPath(window.location.pathname)
    window.addEventListener('popstate', handler)
    const poll = setInterval(() => {
      if (window.location.pathname !== currentPath) {
        setCurrentPath(window.location.pathname)
      }
    }, 500)
    return () => {
      window.removeEventListener('popstate', handler)
      clearInterval(poll)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!config) return null
  if (config.hideOnPaths.some((p) => currentPath.startsWith(p))) return null

  const primary = config.theme.primary

  return (
    <>
      {/* Floating launch button — z-62 keeps it above the spotlight backdrop
          (z-60) so the user always knows Ashraya is there, even mid-tour.
          Hidden while the host flags "shy" (phone sheet/drawer open) unless
          the panel itself is open. */}
      {!(shy && !isOpen) && (
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        aria-label={`Open ${config.appName} help assistant`}
        aria-expanded={isOpen}
        data-tour-id="ashraya-bubble"
        data-testid="ashraya-bubble"
        className="fixed bottom-4 right-4 z-[62] h-14 w-14 rounded-full shadow-lg flex items-center justify-center text-white transition-transform hover:scale-105 active:scale-95"
        style={{ backgroundColor: primary }}
      >
        <span aria-hidden="true" className="text-2xl leading-none">
          {isOpen ? '\u2715' : '\uD83D\uDCAC'}
        </span>
      </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div
          role="dialog"
          aria-label={`${config.appName} help assistant`}
          data-testid="ashraya-panel"
          className="fixed bottom-20 right-4 z-[55] w-[360px] max-w-[calc(100vw-2rem)] h-[520px] max-h-[calc(100vh-6rem)] rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
        >
          <ChatPanel onMinimise={() => setIsOpen(false)} />
        </div>
      )}
    </>
  )
}
